import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { AlertCircle, Loader2, Lock, ExternalLink, Eye, EyeOff, Play } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import LanguageSelector from '../components/LanguageSelector';

import { API_BASE, BACKEND_URL } from '../utils/apiClient';

const API = API_BASE;

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  
  // Tenant branding state
  const [tenantBranding, setTenantBranding] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);

  // Check for subdomain or ?tenant= parameter and load tenant branding
  useEffect(() => {
    const loadTenantBranding = async () => {
      try {
        const hostname = window.location.hostname;
        const urlParams = new URLSearchParams(window.location.search);
        const tenantParam = urlParams.get('tenant'); // Support ?tenant=geotrack for testing
        
        let subdomain = null;
        
        // Priority 1: Check URL parameter (for testing)
        if (tenantParam) {
          subdomain = tenantParam;
        }
        // Priority 2: Check actual subdomain
        else {
          const parts = hostname.split('.');
          
          // For preview URLs like operations-hub-5.preview.emergentagent.com, skip subdomain check
          // Also skip for tmbsaas.com base domain
          if (hostname.includes('preview.emergentagent.com') || hostname === 'localhost' || hostname === 'tmbsaas.com' || hostname === 'www.tmbsaas.com') {
            setLoadingTenant(false);
            return;
          }
          
          // If hostname has subdomain (more than 2 parts for .com, or 3+ for co.uk etc.)
          if (parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'app') {
            subdomain = parts[0];
          }
        }
        
        // Fetch tenant branding if we have a subdomain
        if (subdomain) {
          const response = await axios.get(`${API}/tenant/info?subdomain=${subdomain}`);
          setTenantBranding(response.data);
        }
      } catch (err) {
        console.log('No tenant branding found for this subdomain');
      } finally {
        setLoadingTenant(false);
      }
    };
    
    loadTenantBranding();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      navigate('/');
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        setError('Erreur reseau : impossible de joindre le serveur. Verifiez votre connexion internet.');
      } else {
        setError(err.response?.data?.detail || t('auth.invalidCredentials'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setError('');
    setDemoLoading(true);
    try {
      await login('demo@gembacoach.com', 'Demo123!');
      navigate('/');
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        setError('Erreur reseau : impossible de joindre le serveur. Verifiez votre connexion internet.');
      } else {
        setError(`Erreur de connexion (${err.response?.status || 'inconnue'}). Veuillez reessayer.`);
      }
    } finally {
      setDemoLoading(false);
    }
  };

  // Use tenant branding if available
  const brandName = tenantBranding?.name || t('app.name');
  const brandLogo = tenantBranding?.logo_url || '/logo.png';
  const primaryColor = tenantBranding?.primary_color || '#0E346E';
  const accentColor = '#B98D32';
  const welcomeMessage = tenantBranding?.welcome_message;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
         style={{
           background: `linear-gradient(135deg, #0A1628 0%, ${primaryColor} 50%, #1E4A8E 100%)`
         }}>
      {/* Subtle geometric pattern overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M40 40L20 20v40l20-20zm0 0l20-20v40L40 40z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Gradient orbs for premium effect */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#B98D32]/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#0E346E]/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector variant="minimal" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <img 
            src={brandLogo} 
            alt={brandName} 
            className="w-28 h-28 mx-auto mb-5 object-contain drop-shadow-lg bg-white rounded-xl p-2"
            onError={(e) => { e.target.src = '/logo.png'; }}
          />
          <h1 className="text-4xl font-bold text-white font-['Barlow_Condensed'] tracking-widest mb-3">
            {brandName}
          </h1>
          
          {/* Tagline - 2 lignes */}
          {welcomeMessage ? (
            <p className="text-[#B98D32] font-semibold text-lg tracking-wide">
              {welcomeMessage}
            </p>
          ) : (
            <>
              <p className="text-[#B98D32] font-semibold text-lg tracking-wide">
                {t('app.tagline1')}
              </p>
              <p className="text-slate-300 text-sm mt-1">
                {t('app.tagline2')}
              </p>
            </>
          )}

          {/* Message d'autorité - only show if no tenant branding */}
          {!tenantBranding && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-slate-400 text-xs italic">
                {t('app.authority')}
              </p>
            </div>
          )}
        </div>

        {/* Login Card */}
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
          <CardContent className="pt-8 pb-6 px-8">
            {/* Card Header */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800 font-['Barlow_Condensed'] tracking-wide">
                {t('auth.accessPortal')}
              </h2>
              <div className="w-16 h-1 bg-[#B98D32] mx-auto mt-2 rounded-full" />
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-gray-700 font-medium">
                  {t('auth.email')}
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  required
                  className="h-12 bg-gray-50 border-2 border-gray-200 focus:border-[#0E346E] focus:ring-[#0E346E]/20 text-gray-900 placeholder:text-gray-400"
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-gray-700 font-medium">
                  {t('auth.password')}
                </Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth.passwordPlaceholder')}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    className="h-12 bg-gray-50 border-2 border-gray-200 focus:border-[#0E346E] focus:ring-[#0E346E]/20 text-gray-900 placeholder:text-gray-400 pr-12"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors p-1"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-14 bg-gradient-to-r from-[#0E346E] to-[#1E4A8E] hover:from-[#0A2850] hover:to-[#0E346E] text-white font-bold uppercase tracking-wider text-base shadow-lg hover:shadow-xl transition-all duration-300"
                disabled={loading || demoLoading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.loginButton')
                )}
              </Button>

              {/* Demo Access Separator */}
              {!tenantBranding && (
                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-3 text-gray-400 uppercase tracking-wider">ou</span>
                  </div>
                </div>
              )}

              {/* Demo Button */}
              {!tenantBranding && (
                <Button
                  type="button"
                  onClick={handleDemoLogin}
                  className="w-full h-12 bg-gradient-to-r from-[#B98D32] to-[#D4A843] hover:from-[#A07A2A] hover:to-[#B98D32] text-white font-semibold tracking-wide shadow-md hover:shadow-lg transition-all duration-300"
                  disabled={loading || demoLoading}
                  data-testid="demo-login-btn"
                >
                  {demoLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connexion en cours...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Tester la démo
                    </>
                  )}
                </Button>
              )}

              {/* Security message */}
              <div className="flex items-center justify-center gap-2 text-gray-500 text-xs pt-2">
                <Lock className="w-3.5 h-3.5" />
                <span>{t('auth.secureConnection')}</span>
              </div>
            </form>

            {/* Contact link */}
            <div className="mt-6 pt-5 border-t border-gray-100 text-center">
              <p className="text-gray-500 text-sm">
                {t('auth.needAccess')}
              </p>
              <a 
                href="https://www.tmbsaas.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[#0E346E] hover:text-[#B98D32] font-semibold text-sm mt-1 transition-colors"
              >
                {t('auth.contactTM')}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 text-xs">
            {t('app.copyright')}
          </p>
          <p className="text-slate-500 text-xs mt-1">
            {t('app.allRightsReserved')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
